import objectUtils from '../../utils/object-utils'
import stringUtils from '../../utils/string-utils'
import { dataApi } from '../../services/dataApi'
import { toModel } from '../../services/normalizer'
import AppMessage from '../../models/AppMessage'
import Collection from '../../models/Collection'
import {
  http,
  resolveUriContextFromResourceName,
  parseResourceNameFromModelObject,
  buildModelObject
} from './helpers'
import { defaultPageSize } from './globals'

// TODO: Will be accessed from config !!!
const debug = false
const moduleName = 'ADMIN-DATA'

// Active org context header
const HEADER_NAME_ACTIVE_ORG_ID = 'X-ROUTELEAF-ACTIVE-ORG-ID'

// -----------------------------------------------------------------------------
// Actions
// -----------------------------------------------------------------------------
//
// The "context" parameter is implicitly injected into every
// "store.dispatch()" invocation made by the components. Use the functions
// and data exposed by the context object, in order to implement the
// action logic.
//
// The context object will expose:
//
// context: {
//   commit(),        [use this function to invoke a mutation]
//   dispatch(),      [use this function to invoke an action]
//   getters,         [use this function to invoke a getter]
//   rootState,       [access to the full state]
//   state,           [access to the local state of this module]
// }
// -----------------------------------------------------------------------------

export default {

  // -------------------------------------------- Direct access to RouteLeaf API

  // options = {
  //   method,     --> 'get\load|post|put|delete'
  //   uri,
  //   headers,
  //   opts,
  //   to_model    --> set to false to skip model hydration
  // }
  //
  // opts: {
  //   params: <Object>,    --> send query parameters
  //   bodyData: <Object>,  --> send body data
  //   all: <Boolean>,      --> request all data (no pagination)
  //   force: <Boolean>,    --> set to true to force a new load even if data exists (for 'load' method only)
  //   resource: <String>,  --> used for 'load' method only
  // }
  async dataApi (context, options = {}) {
    const { rootState, getters, commit } = context
    const method = options.method || 'get'
    const uri = options.uri
    const headerOpts = options.headers || {}
    const normalize = (options.to_model === false) ? false : true // eslint-disable-line no-unneeded-ternary
    const opts = options.opts || {}

    // Add auth and active org ID headers
    const token = rootState.auth.authToken
    const activeOrgId = rootState.auth.user.active_org_id
    const headers = Object.assign({ authorization: `Bearer ${token}` }, headerOpts)
    if (activeOrgId) headers[HEADER_NAME_ACTIVE_ORG_ID] = activeOrgId
    Object.assign(opts, { headers })

    if (debug) console.log(`[${moduleName}] Data API - ${method.toUpperCase()} "${uri}" with opts:`, opts)

    try {
      switch (method) {
        case 'get': {
          const payload = await dataApi.get(uri, opts)
          return (normalize) ? toModel(payload) : payload
        }
        case 'post': {
          const payload = await dataApi.post(uri, opts)
          return (normalize) ? toModel(payload) : payload
        }
        case 'put': {
          const payload = await dataApi.put(uri, opts)
          return (normalize) ? toModel(payload) : payload
        }
        case 'delete': {
          const payload = await dataApi.delete(uri, opts)
          return payload
        }
        case 'load': {
          // const all = opts.all
          const force = opts.force
          const resource = opts.resource
          if (!resource) return false

          const existing = getters.loadedData(resource)

          // TODO - Add pagination logic here !!!

          if (!existing || force) {
            const payload = await dataApi.get(uri, opts)
            const result = (normalize) ? toModel(payload) : payload
            commit('setData', { resource, data: result })
          }

          return getters.loadedData(resource)
        }
        default: return false
      }
    } catch (err) {
      if (debug) console.log(`[${moduleName}] Data API - Error:`, err.message)
      throw err
    }
  },

  // ------------------------------------------------------ Loaded data handling

  // options = {
  //   resource: <resource_name>,
  //   params: <params_object>,
  //   id: <item_id>,              --> for item requests
  //   all: true | false,          --> default: false
  //   force: true | false         --> default: false
  // }
  async loadData (context, options = {}) {
    const { commit, dispatch, getters } = context
    const itemID = options.id
    const all = options.all
    const force = options.force
    const resource = options.resource
    if (!resource) return false

    const existing = getters.data(resource)

    const params = (options.params) ? Object.assign({}, options.params) : {}
    if (!itemID && !all) {
      if (!objectUtils.has(params, 'skip')) params.skip = 0
      if (!objectUtils.has(params, 'limit')) params.limit = defaultPageSize
    }
    const opts = { resource, params }
    if (itemID) opts.id = itemID

    if (!existing || force) {
      if (debug) {
        if (itemID) console.log(`[${moduleName}] Loading "${resource}" data with item ID:`, itemID)
        else console.log(`[${moduleName}] Loading "${resource}" data`)
      }
      const result = await dispatch('fetchData', opts)
      commit('setData', { resource, data: result })
      return result
    }

    return true
  },

  // options = {
  //   resource: <resource_name>,
  //   params: <params_object>
  // }
  async loadDataNext (context, options = {}) {
    const { commit, getters, dispatch } = context
    const resource = options.resource
    if (!resource) return false

    const existing = getters.data(resource)
    const next = existing.meta.next
    const params = (options.params) ? Object.assign({}, options.params) : {}

    if (next) {
      Object.assign(params, { skip: next.skip, limit: next.limit })
      const opts = { resource, params }

      const collection = await dispatch('fetchData', opts)

      // Append next page of results to collection, set new meta info...
      const updatedCollection = new Collection()
      updatedCollection.meta = collection.meta
      updatedCollection.items = (collection.items.length > 0)
        ? existing.items.concat(collection.items)
        : existing.items

      commit('setData', { resource, data: updatedCollection })

      return updatedCollection
    } // end-if (next)

    return false
  },

  // -------------------------------------------------------- Sync data handling

  // dataOpts = <the_model_object>
  //     -or-
  // dataOpts = {
  //   resource: <resource_name>,
  //   data: <the_model_object> | { ...fields },
  //   nested: <prop_name>         --> a nested property of the registry (OPTIONAL)
  //   replace: true | false,      --> Replace the existing entry entirely (default: false = merges the data)
  // }
  syncData (context, dataOpts = {}) {
    const { commit } = context
    const item = (dataOpts.model) ? dataOpts : dataOpts.data
    const resource = dataOpts.resource || parseResourceNameFromModelObject(item)
    const nested = dataOpts.nested
    const replace = dataOpts.replace
    if (!resource || !item) return Promise.reject(false) // eslint-disable-line prefer-promise-reject-errors

    if (debug) console.log(`[${moduleName}] Syncing state with "${resource}" data:`, item)
    commit('syncDataItem', { resource, nested, data: item, replace })
  },

  // ------------------------------------------------------- Clear data handling

  // options = {
  //   resource: <resource_name> | null    --> omit to handle all resources
  //   editing: true | false               --> include editing registry (default: false)
  // }
  clearData (context, options = {}) {
    const { state, commit } = context
    const resource = options.resource
    const includeEditing = options.editing
    const scope = ['loaded']
    if (includeEditing) scope.push('editing')
    const scopeToDisplay = scope.join(', ')

    // Clear data for a single resource
    if (resource) {
      if (debug) console.log(`[${moduleName}] Clearing "${resource}" data (${scopeToDisplay})...`)
      if (includeEditing) commit('clearItemEditing', resource)
      commit('clearData', resource)

    // Clear data for all resources
    } else {
      if (debug) console.log(`[${moduleName}] Clearing data (${scopeToDisplay})...`)
      Object.keys(state.dataRegistry).forEach((name) => {
        if (includeEditing) commit('clearItemEditing', name)
        commit('clearData', name)
      })
    }
  },

  // -------------------------------------------------------- Core data handlers

  // options = {
  //   resource: <resource_name>,
  //   id: <item_id>,              --> for item requests
  //   params: <params_object>
  // }
  async fetchData (context, options = {}) {
    const { rootState } = context
    const resource = options.resource
    const itemID = options.id
    const params = options.params

    // Build request
    const apiResource = resolveUriContextFromResourceName(resource)
    const uri = (itemID) ? `/${apiResource}/${itemID}` : `/${apiResource}`

    if (debug) {
      if (itemID) console.log(`[${moduleName}] Fetching item (${itemID}) of type "${resource}" with params:`, params)
      else console.log(`[${moduleName}] Fetching collection of type "${resource}", with params:`, params)
      console.log(`[${moduleName}] Resolved to URI:`, uri)
    }

    try {
      const token = rootState.auth.authToken
      const activeOrgId = rootState.auth.user.active_org_id
      const headers = { authorization: `Bearer ${token}` }
      if (activeOrgId) headers[HEADER_NAME_ACTIVE_ORG_ID] = activeOrgId

      const payload = await http.get(uri, { headers, ...params })
      return toModel(payload)
    } catch (error) {
      if (debug) {
        if (itemID) console.log(`[${moduleName}] Error fetching item (${itemID}) of type "${resource}" =>`, error)
        else console.log(`[${moduleName}] Error fetching collection of type "${resource}" =>`, error)
      }
      throw error
    }
  },

  // dataOpts = {
  //   resource: <resource_name>,
  //   data: { ...fields }         --> optional (to override default draft fields)
  // }
  async createDraft (context, dataOpts = {}) {
    const resource = dataOpts.resource
    const draftFields = dataOpts.data || {}
    if (!resource) return Promise.reject(false) // eslint-disable-line prefer-promise-reject-errors

    if (debug) console.log(`[${moduleName}] Creating a draft item of type "${resource}" with data:`, draftFields)

    // Apply default fields
    const modelName = stringUtils.toTitleCase(resource)
    const draftData = Object.assign({ name: `New ${modelName}` }, draftFields)

    // Create the requested draft object
    const draft = buildModelObject(resource, draftData)
    if (!draft) {
      const errorMessage = `[${moduleName}] Cannot create a draft object of type "${resource}"`
      if (debug) console.log(errorMessage)
      throw new Error(errorMessage)
    }

    return draft
  },

  // dataOpts = <the_model_object>
  //     -or-
  // dataOpts = {
  //   resource: <resource_name>,
  //   messageResource: <resource_name>,        --> for message association override
  //   data: <the_model_object> | { ...fields },
  //   noFeedback: true                         --> set to true to disable messages
  // }
  async createData (context, dataOpts = {}) {
    const { rootState, dispatch } = context
    const item = (dataOpts.model) ? dataOpts : dataOpts.data
    const resource = dataOpts.resource || parseResourceNameFromModelObject(item)
    const messageResource = (dataOpts.messageResource) ? dataOpts.messageResource : resource
    const feedback = dataOpts.noFeedback !== true

    if (!resource) return Promise.reject(false) // eslint-disable-line prefer-promise-reject-errors

    // Build request
    const apiResource = resolveUriContextFromResourceName(resource)
    const uri = `/${apiResource}`
    const data = item

    if (debug) console.log(`[${moduleName}] Creating a resource of type "${resource}" with input:`, data)

    try {
      const token = rootState.auth.authToken
      const activeOrgId = rootState.auth.user.active_org_id
      const headers = { authorization: `Bearer ${token}` }
      if (activeOrgId) headers[HEADER_NAME_ACTIVE_ORG_ID] = activeOrgId

      const payload = await http.post(uri, { headers, bodyData: data })
      const createdItem = toModel(payload)
      if (debug) console.log(`[${moduleName}] Created "${resource}" item:`, createdItem)

      // Register feedback message
      if (feedback) {
        const message = new AppMessage({
          source: createdItem,
          status_code: 201,
          severity: 'success'
        })
        dispatch('registerMessageForItemEditing', { resource: messageResource, message })
      }

      return createdItem
    } catch (error) {
      if (debug) console.log(`[${moduleName}] Error creating item of type "${resource}" =>`, error)

      // Register feedback message
      if (feedback) {
        const message = new AppMessage({
          source: error,
          status_code: error.status,
          severity: 'error',
          text: error.messages
        })
        dispatch('registerMessageForItemEditing', { resource: messageResource, message })
      }

      throw error
    }
  },

  // dataOpts = <the_model_object>
  //     -or-
  // dataOpts = {
  //   resource: <resource_name>,
  //   messageResource: <resource_name>,        --> for message association override
  //   data: <the_model_object> | { ...fields }
  //   noFeedback: true                         --> set to true to disable messages
  // }
  async updateData (context, dataOpts = {}) {
    const { rootState, dispatch } = context
    const item = (dataOpts.model) ? dataOpts : dataOpts.data
    const itemID = item.id
    const resource = dataOpts.resource || parseResourceNameFromModelObject(item)
    const messageResource = (dataOpts.messageResource) ? dataOpts.messageResource : resource
    const feedback = dataOpts.noFeedback !== true
    if (!resource) return Promise.reject(false) // eslint-disable-line prefer-promise-reject-errors

    // Build request
    const apiResource = resolveUriContextFromResourceName(resource)
    const uri = `/${apiResource}/${itemID}`
    const data = (item.toUpdate) ? item.toUpdate() : item

    if (debug) console.log(`[${moduleName}] Updating a resource of type "${resource}" with input:`, data)

    try {
      const token = rootState.auth.authToken
      const activeOrgId = rootState.auth.user.active_org_id
      const headers = { authorization: `Bearer ${token}` }
      if (activeOrgId) headers[HEADER_NAME_ACTIVE_ORG_ID] = activeOrgId

      const payload = await http.post(uri, { headers, bodyData: data })
      const updatedItem = toModel(payload)
      if (debug) console.log(`[${moduleName}] Updated "${resource}" item:`, updatedItem)

      // Sync updated item with state
      // dispatch('syncData', updatedItem)

      // Register feedback message
      if (feedback) {
        const message = new AppMessage({
          source: updatedItem,
          status_code: 200,
          severity: 'success'
        })
        dispatch('registerMessageForItemEditing', { resource: messageResource, message })
      }

      return updatedItem
    } catch (error) {
      if (debug) console.log(`[${moduleName}] Error updating item (${itemID}) of type "${resource}" =>`, error)

      // Register feedback message
      if (feedback) {
        const message = new AppMessage({
          source: error,
          status_code: error.status,
          severity: 'error',
          text: error.messages
        })
        dispatch('registerMessageForItemEditing', { resource: messageResource, message })
      }

      throw error
    }
  },

  // dataOpts = <the_model_object>
  //     -or-
  // dataOpts = {
  //   resource: <resource_name>,
  //   messageResource: <resource_name>,        --> for message association override
  //   data: <the_model_object> | { ...fields }
  //   noFeedback: true                         --> set to true to disable messages
  // }
  async deleteData (context, dataOpts = {}) {
    const { rootState, dispatch } = context
    const item = (dataOpts.model) ? dataOpts : dataOpts.data
    const itemID = item.id
    const itemLabel = item.name || itemID
    const resource = dataOpts.resource || parseResourceNameFromModelObject(item)
    const messageResource = (dataOpts.messageResource) ? dataOpts.messageResource : resource
    const feedback = dataOpts.noFeedback !== true
    if (!resource || !itemID) return Promise.reject(false) // eslint-disable-line prefer-promise-reject-errors

    // Build request
    const apiResource = resolveUriContextFromResourceName(resource)
    const uri = `/${apiResource}/${itemID}`

    if (debug) console.log(`[${moduleName}] Deleting a resource of type "${resource}" with id:`, itemID)

    try {
      const token = rootState.auth.authToken
      const activeOrgId = rootState.auth.user.active_org_id
      const headers = { authorization: `Bearer ${token}` }
      if (activeOrgId) headers[HEADER_NAME_ACTIVE_ORG_ID] = activeOrgId

      await http.delete(uri, { headers })
      if (debug) console.log(`[${moduleName}] Deleted "${resource}" with id:`, itemID)

      // Register feedback message
      if (feedback) {
        const message = new AppMessage({
          source: { id: itemID, name: itemLabel },
          status_code: 204,
          severity: 'success'
        })
        dispatch('registerMessageForItemEditing', { resource: messageResource, message })
      }

      return { id: itemID }
    } catch (error) {
      if (debug) console.log(`[${moduleName}] Error deleting item (${itemID}) of type "${resource}" =>`, error)

      // Register feedback message
      if (feedback) {
        const message = new AppMessage({
          source: error,
          status_code: error.status,
          severity: 'error',
          text: error.messages
        })
        dispatch('registerMessageForItemEditing', { resource: messageResource, message })
      }

      throw error
    }
  },

  // ------------------------------------------------------ Item editing actions

  // dataOpts = <the_model_object>
  //     -or-
  // dataOpts = {
  //   resource: <resource_name>,
  //   data: <the_model_object> | { ...fields }
  // }
  setItemToEdit (context, dataOpts) {
    const item = (dataOpts.model) ? dataOpts : dataOpts.data
    const resource = dataOpts.resource || parseResourceNameFromModelObject(item)
    const from = (context.rootState.route) ? context.rootState.route : null
    if (!resource) return false

    if (debug) console.log(`[${moduleName}] Setting "${resource}" item to edit =>`, item)
    return context.commit('setItemToEdit', { resource, data: item, from })
  },

  // dataOpts = <the_model_object>
  //     -or-
  // dataOpts = {
  //   resource: <resource_name>,
  //   data: <the_model_object> | { ...fields },
  //   nested: <prop_name>         --> a nested property of the registry (OPTIONAL)
  //   replace: true | false,      --> Replace the existing entry entirely (default: false = merges the data)
  // }
  syncItemEditing (context, dataOpts = {}) {
    const { commit } = context
    const item = (dataOpts.model) ? dataOpts : dataOpts.data
    const resource = dataOpts.resource || parseResourceNameFromModelObject(item)
    const nested = dataOpts.nested
    const replace = dataOpts.replace
    if (!resource || !item) return Promise.reject(false) // eslint-disable-line prefer-promise-reject-errors

    if (debug) console.log(`[${moduleName}] Syncing editing state with "${resource}" data:`, item)
    commit('syncItemEditing', { resource, nested, data: item, replace })
  },

  clearItemEditing (context, resource) {
    context.commit('clearItemEditing', resource)
    return Promise.resolve(true)
  },

  // info = {
  //   resource: <resource_name>,
  //   message: <the_message>
  // }
  registerMessageForItemEditing (context, info = {}) {
    context.commit('registerMessageForItemEditing', info)
    context.commit('advanceMessageForItemEditing', info.resource)
  },

  resolveMessageForItemEditing (context, resource) {
    context.commit('resolveMessageForItemEditing', resource)
    context.commit('advanceMessageForItemEditing', resource)
  }
}
